import React, { useState, useEffect, Suspense } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import default_user from '../assets/images/default_user.webp'; // Import the default image
import msgload from '../assets/images/ds.gif';

const CollectionMenu = ({ config, searchQuery, sortOption }) => {
  const [userLists, setUserLists] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalFollowingPostCount, setFollowingPostCount] = useState(0);
  const [totalFanPostCount, setFanPostCount] = useState(0);
  const [totalFanCount, setTotalFanCount] = useState(0);
  const [restrictedCount, setRestrictedCount] = useState(0);
  const [blockedCount, setBlockedCount] = useState(0);
  const [followingProfiles, setFollowingProfiles] = useState([]);
  const [fanProfiles, setFanProfiles] = useState([]); 
  const [filteredLists, setFilteredLists] = useState([]);
  const [activeListId, setActiveListId] = useState(null);
  const [loading, setLoading] = useState(true); // Loading state to show the loader
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/Login');
      return;
    }

    // Fetching the data and setting the loading state accordingly
    const fetchData = async () => {
      try {
        // Fetch following users
        const followingResponse = await axios.get(`${config.baseURL}/following-users-count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (followingResponse.data.success) {
          const { total_count, user_profiles, total_posts } = followingResponse.data;
          setTotalCount(total_count);
          setFollowingPostCount(total_posts);
          setFollowingProfiles(user_profiles);
        }

        // Fetch fans users
        const fansResponse = await axios.get(`${config.baseURL}/fans-users-count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (fansResponse.data.success) {
          const { total_count, user_profiles, total_posts } = fansResponse.data;
          setTotalFanCount(total_count);
          setFanPostCount(total_posts);
          setFanProfiles(user_profiles);
        }

        // Fetch user lists
        const listsResponse = await axios.get(`${config.baseURL}/get-users-list`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (listsResponse.data.success) {
          const { data } = listsResponse.data;
          setUserLists(data);
          setFilteredLists(data); // Initialize with all lists
        }

        // Fetch restricted users
        const restrictedResponse = await axios.get(`${config.baseURL}/total-restricted-user`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (restrictedResponse.data.success) {
          const { total_restricted_user } = restrictedResponse.data;
          setRestrictedCount(total_restricted_user);
        }

        // Fetch blocked users
        const blockedResponse = await axios.get(`${config.baseURL}/total-blocked-user`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (blockedResponse.data.success) {
          const { total_blocked_user } = blockedResponse.data;
          setBlockedCount(total_blocked_user);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false); // Set loading to false once all data is fetched
      }
    };

    fetchData();
  }, [config.baseURL, navigate]);

  useEffect(() => {
    let updatedLists = [...userLists];

    // Filter based on searchQuery
    if (searchQuery) {
      const lowerCaseQuery = searchQuery.toLowerCase();
      updatedLists = updatedLists.filter(list => 
        list.title.toLowerCase().includes(lowerCaseQuery)
      );
    }

    // Sort based on sortOption
    if (sortOption === 'name') {
      updatedLists.sort((a, b) => a.title.localeCompare(b.title));
    } else if (sortOption === 'ascendinglist') {
      updatedLists.sort((a, b) => a.user_count - b.user_count);
    } else if (sortOption === 'descendinglist') {
      updatedLists.sort((a, b) => b.user_count - a.user_count);
    }

    // Update the filteredLists state
    setFilteredLists(updatedLists);
  }, [searchQuery, sortOption, userLists]);

  const isActive = (path) => location.pathname === path;

  const pathParts = location.pathname.split('/');
  const currentListId = pathParts[2]; // Extract ID from URL
  const currentListSlug = pathParts[3]; // Extract slug from URL

  const handleListClick = (list, userId) => {
    const listTitleSlug = list.title.toLowerCase().replace(/\s+/g, '-');
    const newUrl = `/user-list/${list.id}/${listTitleSlug}`;
    // Navigate and reload the page
    window.location.href = newUrl;
  };

  return (
    <div>
      {/* Suspense Wrapper */}
      <Suspense fallback={<div className="loader-overlay sdc"><img src={msgload} alt="Loading..." /></div>}>
        {loading ? (
          <div className="loader-overlay sdc">
            <img src={msgload} alt="Loading..." />
          </div>
        ) : (
          <ul>
            {/* Menu items */}
            <li>
              <Link className={isActive('/collections') ? "active_profile_menu" : ""} to="/collections"> 
                <div className="list_item_text_menu">
                  <div className="text_collection">Following</div>
                  <div className="text_collection_bottom">
                    <ul className="nav_menu">
                      <li>{totalCount} User{totalCount !== 1 ? 's' : ''}</li>
                      <li>{totalFollowingPostCount} posts</li>
                    </ul>
                  </div>
                </div>
                <div className="profile_pic_collection">
                  {followingProfiles.map((profile, index) => (
                    <img
                      key={index}
                      src={profile.profile || default_user} // Use default image if profile is null
                      alt="User profile"
                      style={{ width: 40, height: 40, borderRadius: "50%" }}
                    />
                  ))}
                </div> 
              </Link>
            </li>

            <li>
              <Link className={isActive('/fans') ? "active_profile_menu" : ""} to="/fans"> 
                <div className="list_item_text_menu">
                  <div className="text_collection">Fans</div>
                  <div className="text_collection_bottom">
                    <ul className="nav_menu">
                      <li>{totalFanCount} User{totalFanCount !== 1 ? 's' : ''}</li>
                      <li>{totalFanPostCount} posts</li>
                    </ul>
                  </div>
                </div>
                <div className="profile_pic_collection">
                  {fanProfiles.map((profile, index) => (
                    <img
                      key={index}
                      src={profile.profile || default_user} // Use default image if profile is null
                      alt="User profile"
                      style={{ width: 40, height: 40, borderRadius: "50%" }}
                    />
                  ))}
                </div>                                     
              </Link>
            </li>  

            <li>
             <Link className={isActive('/restricted') ? "active_profile_menu" : ""} to="/restricted"> 
                <div className="list_item_text_menu">
                  <div className="text_collection">Restricted</div>
                  <div className="text_collection_bottom">
                    <ul className="nav_menu">
                      <li>{restrictedCount} User{restrictedCount !== 1 ? 's' : ''}</li>
                    </ul>
                  </div>
                </div>
              </Link>
            </li>

            <li>
              <Link className={isActive('/blocked') ? "active_profile_menu" : ""} to="/blocked"> 
                <div className="list_item_text_menu">
                  <div className="text_collection">Blocked</div>
                  <div className="text_collection_bottom">
                    <ul className="nav_menu">
                      <li>{blockedCount} User{blockedCount !== 1 ? 's' : ''}</li>
                    </ul>
                  </div>
                </div>
              </Link>
            </li>

            {filteredLists.length > 0 ? (
              filteredLists.map(list => {
                const listTitleSlug = list.title.toLowerCase().replace(/\s+/g, '-');
                const isActive = list.id.toString() === currentListId && listTitleSlug === currentListSlug;
                return (
                  <li
                    key={list.id}
                    className={isActive ? 'active_profile_menu' : ''} 
                  >
                    <a
                      className="hover_menu"
                      onClick={() => handleListClick(list, list.users.id)}
                    >
                      <div className="list_item_text_menu">
                        <div className="text_collection">{list.title}</div> 
                        <div className="text_collection_bottom">
                          <ul className="nav_menu">
                            <li>{list.user_count} user{list.user_count !== 1 ? 's' : ''}</li>
                            <li>{list.total_posts} posts</li>
                          </ul>
                        </div>
                      </div>
                      <div className="profile_pic_collection"> 
                        {list.users.length > 0 ? (
                          list.users.map((user, index) => (
                            <img
                              key={index}
                              src={user.profile_image || default_user}
                              alt="User profile"
                              style={{ width: 40, height: 40, borderRadius: "50%" }}
                            />
                          ))
                        ) : (
                          <></>
                          // <p>No profiles available</p>
                        )}
                      </div> 
                    </a>
                  </li>
                );
              })
            ) : (
              <li></li>
            )}
          </ul>
        )}
      </Suspense>
    </div>
  );
};

export default CollectionMenu;
